import React, {ReactElement, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {type TypedTranslation} from '../../../../i18n';
import {AnimationString} from '../../../../incase-domain/common/animationString';
import Label from '../../../../incase-domain/components/label/Label';
import DropdownMenuCompact from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Button from '../../../../incase-domain/components/button/Button';

export type AnimationSelectCategory = 'all' | 'entry' | 'attention';

interface AnimationOption {
    label: string;
    value: AnimationString | '';
}

const ENTRY_ANIMATIONS: Array<AnimationString> = [
    'fadeIn',
    'fadeInDown',
    'fadeInLeft',
    'fadeInRight',
    'fadeInUp',
    'bounceIn',
    'bounceInDown',
    'bounceInLeft',
    'bounceInRight',
    'bounceInUp',
    'zoomIn',
    'zoomInDown',
    'zoomInLeft',
    'zoomInRight',
    'zoomInUp',
    'slideInDown',
    'slideInLeft',
    'slideInRight',
    'slideInUp',
    'flipInX',
    'flipInY',
    'rotateIn',
    'rollIn',
    'lightSpeedIn',
    'jackInTheBox',
];

const ATTENTION_ANIMATIONS: Array<AnimationString> = [
    'bounce',
    'flash',
    'pulse',
    'rubberBand',
    'shake',
    'swing',
    'tada',
    'wobble',
    'jello',
];

function getAnimationsByCategory(category: AnimationSelectCategory): AnimationString[] {
    switch (category) {
        case 'entry':
            return ENTRY_ANIMATIONS;
        case 'attention':
            return ATTENTION_ANIMATIONS;
        default:
            return [...ENTRY_ANIMATIONS, ...ATTENTION_ANIMATIONS];
    }
}

interface AnimationInSelectProps {
    animationIn: string;
    highlightedElementChangeHandler: (animation: AnimationString | '') => void;
    label?: string;
    category?: AnimationSelectCategory;
}

function AnimationInSelect(props: AnimationInSelectProps): ReactElement {
    const {animationIn, highlightedElementChangeHandler, category = 'all', label} = props;
    const {t} = useTranslation();

    const animationOptions: AnimationOption[] = useMemo(() => {
        const anims = getAnimationsByCategory(category);
        const options: AnimationOption[] = [{label: t('designSidepanel.animations.noAnimation'), value: ''}];
        anims.forEach((a) => {
            const key = `designSidepanel.animations.${a}`;
            const translated = t(key as TypedTranslation);
            options.push({label: translated !== key ? translated : a, value: a});
        });
        return options;
    }, [t, category]);

    const selectedValue = useMemo(() => animationOptions.find((el) => el.value === animationIn), [animationOptions, animationIn]);

    const labelText = label ?? t('designSidepanel.animationType');

    return (
        <div className="u-flex u-h-between u-v-center u-gap-2">
            <Label>{labelText}</Label>
            <DropdownMenuCompact
                items={[
                    animationOptions.map((el) => ({
                        label: el.label,
                        onClick: () => highlightedElementChangeHandler(el.value as AnimationString),
                    })),
                ]}
            >
                <Button className="c-sidepanel__dropdown_button" size="xs" variant="soft" trailing="solar:alt-arrow-down-linear">
                    {selectedValue?.label || t('designSidepanel.animations.noAnimation')}
                </Button>
            </DropdownMenuCompact>
        </div>
    );
}

export default AnimationInSelect;
